import { DoubleRightOutlined } from '@ant-design/icons'
import { css } from '@emotion/react'
import { colors } from '@kmns-internal/furikake/tokens'
import { LocationOn } from '@mui/icons-material'
import { TraceKey } from '@ulysses-inc/harami_api_client'
import { Alert, Button, Checkbox, Flex, Input, Select, Spin } from 'antd'
import { useState } from 'react'
import PlaceGroupSVG from 'src/assets/icons/place_groups.svg?react'
import { ErrorMessage } from 'src/components/errorMessage/ErrorMessage'
import { useLimitedFeatureAvailability } from 'src/features/featureAvailability/useLimitedFeatureAvailability'
import { logTraceKeyFilterEvent } from 'src/features/firebase/featureSpecificEventLog/traceKeyFilterEvent'
import { ReportListFiltersDrawerFieldset } from 'src/features/reports/listBetaFilters/drawer/ReportListFiltersDrawerFieldset'
import {
  // 名前がコンポーネントと重複するため
  ReportListFilters as ReportListFiltersType,
  isValidFilterValueOf,
} from 'src/features/reports/listBetaFilters/types'
import {
  initialFilters,
  traceKeyConditionArraySchema,
  useReportListFilters,
} from 'src/features/reports/listBetaFilters/useReportListFilters'
import { useQueryPlaceNodes } from 'src/features/tanStackQuery/commonQueries/useQueryPlaceNodes'
import { useQueryUserPlaceNodes } from 'src/features/tanStackQuery/commonQueries/useQueryUserPlaceNodes'
import {
  TraceKeyConditionFilter,
  useTraceKeyFilter,
} from './hooks/useTraceKeyFilter'
import { ReportListFiltersTraceKey } from './tracekey/ReportListFiltersTraceKey'

const deviatedAnswerCheckboxGroupOptions: {
  label: string
  value: ReportListFiltersType['deviatedAnswer'][number]
}[] = [
  {
    label: '逸脱あり',
    value: 'present',
  },
  {
    label: '逸脱なし',
    value: 'absent',
  },
]

const statusCheckboxGroupOptions: {
  label: string
  value: ReportListFiltersType['statuses'][number]
}[] = [
  {
    label: '記録中',
    value: 'inCompleted',
  },
  {
    label: '承認待ち',
    value: 'approvalPending',
  },
  {
    label: '差し戻し',
    value: 'approvalRemand',
  },
  {
    label: '完了',
    value: 'completed',
  },
]

type Props = {
  filters: ReportListFiltersType
  onCloseButtonClicked: () => void
  setFilterOf: ReturnType<typeof useReportListFilters>['setFilterOf']
  resetFilterOf: ReturnType<typeof useReportListFilters>['resetFilterOf']
  executeSearch: ReturnType<typeof useReportListFilters>['executeSearch']
  onAfterApply: () => void
}

export const ReportListFiltersDrawer = ({
  filters,
  onCloseButtonClicked,
  setFilterOf,
  resetFilterOf,
  executeSearch,
  onAfterApply,
}: Props) => {
  const { isFeatureAvailable } = useLimitedFeatureAvailability()
  const canUseReportTrace = isFeatureAvailable('reportTrace')

  // 検索キー追加/削除時も適切にエラー表示を制御するため、
  // 検索キーの項目ごとにバリデーション状態を管理
  const [validatedTraceKeyIds, setValidatedTraceKeyIds] = useState<Set<number>>(
    new Set(),
  )

  const userPlaceNodesQuery = useQueryUserPlaceNodes()
  const allPlaceNodesQuery = useQueryPlaceNodes()
  const isPending =
    userPlaceNodesQuery.isPending || allPlaceNodesQuery.isPending
  const isError = userPlaceNodesQuery.isError || allPlaceNodesQuery.isError

  const {
    traceKeyConditionFilters,
    availableTraceKeys,
    addTraceKeyConditionFilter,
    removeTraceKeyConditionFilter,
    updateTraceKeyConditionFilter,
    clearTraceKeyConditionFilters,
    isLoading: isTraceKeysLoading,
  } = useTraceKeyFilter(filters.traceKeyConditions)

  // 検索キーを追加時に従業員フィルターをクリアする
  const handleAddTraceKeyConditionFilter = (traceKey: TraceKey) => {
    setFilterOf.employeeNameOrCode('')
    addTraceKeyConditionFilter(traceKey)
  }

  // 検索キーを削除時にその項目のバリデーション状態を削除
  const handleRemoveTraceKeyConditionFilter = (
    filter: TraceKeyConditionFilter,
  ) => {
    removeTraceKeyConditionFilter(filter)
    setValidatedTraceKeyIds(prev => {
      const next = new Set(prev)
      next.delete(filter.traceKeyId)
      return next
    })
  }

  // 検索キーの値を更新時にその項目のバリデーション状態を削除
  // （適用ボタン押下時のみバリデーション表示するため）
  const handleUpdateTraceKeyConditionFilter = (
    newFilter: TraceKeyConditionFilter,
  ) => {
    updateTraceKeyConditionFilter(newFilter)
    setValidatedTraceKeyIds(prev => {
      const next = new Set(prev)
      next.delete(newFilter.traceKeyId)
      return next
    })
  }

  const hasTraceKeyFilter = traceKeyConditionFilters.length > 0

  if (isError) {
    return <ErrorMessage noMargin>エラーが発生しました</ErrorMessage>
  }

  if (isPending || isTraceKeysLoading) {
    return <Spin />
  }

  // 現状では、現場に未所属のユーザーはすべての現場に属しているのと同じ扱いとなっているため
  const userHasSomeAssignedPlaceNodes = userPlaceNodesQuery.data.length > 0
  const selectablePlaceNodes = userHasSomeAssignedPlaceNodes
    ? userPlaceNodesQuery.data
    : allPlaceNodesQuery.data
  return (
    <div>
      <div css={styles.topButtonsRow}>
        <Button
          icon={<DoubleRightOutlined style={{ fontSize: 12 }} />}
          onClick={onCloseButtonClicked}
          size="small"
        />
        <Button
          css={styles.clearButton}
          onClick={() => {
            // ドロワー内のクリアボタンでは、ドロワー内に入力欄がある項目だけをクリアする仕様である
            // レポート名と期間を保持するため、先に値を取得
            const { reportName, dateFrom, dateTo } = filters

            resetFilterOf.allExceptReportNameAndDateRange()
            clearTraceKeyConditionFilters()
            setValidatedTraceKeyIds(new Set()) // バリデーション状態をリセット

            // レポート名と期間を保持し、それ以外のフィルターを初期化した状態で検索
            const clearedFilters = {
              ...initialFilters,
              reportName,
              dateFrom,
              dateTo,
            }
            executeSearch(clearedFilters)
            onAfterApply()
          }}
        >
          すべてクリア
        </Button>
      </div>

      <div css={styles.restRow}>
        <ReportListFiltersDrawerFieldset title="現場">
          <Select
            css={styles.placeNodeSelect}
            mode="multiple"
            onChange={uuids => {
              setFilterOf.placeNodeUuids(uuids)
            }}
            optionFilterProp="label"
            value={filters.placeNodeUuids}
            placeholder="現場を選択"
            showSearch
          >
            {(selectablePlaceNodes || []).map(placeNode => {
              return (
                <Select.Option
                  key={placeNode.uuid}
                  value={placeNode.uuid}
                  label={placeNode.name}
                >
                  <div css={styles.placeNodesOptionInner}>
                    <div css={styles.placeNodesOptionIcon}>
                      {placeNode.type === 'placeGroup' ? (
                        <PlaceGroupSVG
                          style={{ width: '16px', color: colors.action.active }}
                        />
                      ) : (
                        <LocationOn
                          sx={{ width: 16, color: colors.action.active }}
                        />
                      )}
                    </div>
                    <span>{placeNode.name}</span>
                  </div>
                </Select.Option>
              )
            })}
          </Select>
        </ReportListFiltersDrawerFieldset>
        <ReportListFiltersDrawerFieldset title="逸脱有無">
          <Checkbox.Group
            options={deviatedAnswerCheckboxGroupOptions}
            value={filters.deviatedAnswer}
            onChange={checkedValues => {
              // filterしているのは単に型の都合
              setFilterOf.deviatedAnswer(
                checkedValues.filter(isValidFilterValueOf.deviatedAnswer),
              )
            }}
          />
        </ReportListFiltersDrawerFieldset>
        <ReportListFiltersDrawerFieldset title="ステータス">
          <Checkbox.Group
            options={statusCheckboxGroupOptions}
            value={filters.statuses}
            onChange={checkedValues => {
              // filterしているのは単に型の都合
              setFilterOf.statuses(
                checkedValues.filter(isValidFilterValueOf.status),
              )
            }}
          />
        </ReportListFiltersDrawerFieldset>
        <ReportListFiltersDrawerFieldset title="従業員">
          <Input
            allowClear
            disabled={hasTraceKeyFilter}
            onChange={e => {
              setFilterOf.employeeNameOrCode(e.target.value)
            }}
            placeholder="従業員名または従業員番号"
            value={filters.employeeNameOrCode}
          />
        </ReportListFiltersDrawerFieldset>
        {canUseReportTrace && (
          <ReportListFiltersTraceKey
            traceKeyConditionFilters={traceKeyConditionFilters}
            availableTraceKeys={availableTraceKeys}
            addTraceKeyConditionFilter={handleAddTraceKeyConditionFilter}
            removeTraceKeyConditionFilter={handleRemoveTraceKeyConditionFilter}
            updateTraceKeyConditionFilter={handleUpdateTraceKeyConditionFilter}
            validatedTraceKeyIds={validatedTraceKeyIds}
          />
        )}
        <Flex justify="end" gap={10}>
          <Button onClick={onCloseButtonClicked}>キャンセル</Button>
          <Button
            type="primary"
            onClick={() => {
              // すべての検索キーをバリデーション対象にする
              const allTraceKeyIds = new Set(
                traceKeyConditionFilters.map(f => f.traceKeyId),
              )
              setValidatedTraceKeyIds(allTraceKeyIds)

              // バリデーションチェック
              const isValid = traceKeyConditionArraySchema.isValidSync(
                traceKeyConditionFilters,
              )

              if (!isValid) {
                // エラーがある場合は検索実行しない
                return
              }

              // バリデーション成功時のみ検索実行
              const updatedFilters = {
                ...filters,
                traceKeyConditions: traceKeyConditionFilters,
              }
              logTraceKeyFilterEvent(traceKeyConditionFilters)
              executeSearch(updatedFilters)
              onAfterApply()
            }}
          >
            適用
          </Button>
        </Flex>
        {canUseReportTrace && (
          <Alert
            message="検索キーご利用方法"
            description={
              <>
                ・検索キーを追加すると「従業員」で絞り込みできません
                <br />
                ・レポート記録時にセット選択肢質問で複数選択した場合、その回答は検索できなくなります
              </>
            }
            type="info"
            showIcon
          />
        )}
      </div>
    </div>
  )
}

const styles = {
  topButtonsRow: css`
    align-items: center;
    display: flex;
    justify-content: space-between;
    padding: 16px;
  `,
  clearButton: css`
    align-self: flex-end;
  `,
  placeNodeSelect: css`
    width: 100%;
  `,
  restRow: css`
    display: grid;
    grid-gap: 24px;
    padding: 16px 24px 24px;
  `,
  placeNodesOptionInner: css`
    display: flex;
    align-items: center;
    min-height: 22px;
  `,
  placeNodesOptionIcon: css`
    margin-right: 8px;
    display: flex;
    flex-shrink: 0;
    min-width: 16px;
  `,
}
